/**
 * Field definitions shown in the "Additional Information" dialog on the Lifts
 * page, mirroring what the Route Status page already provides.
 *
 * Copy is customer-approved (PBC) — do not reword without sign-off. Each entry
 * describes a column rendered by LiftDataTable, so if the underlying behaviour
 * changes the definition has to change with it. Current sources of truth:
 *
 *   Last Updated        -> DUO_METRIC_REPORTING.DATE_UPDATED, advanced on
 *       every heartbeat regardless of GPS.
 *   Asset Tracking      -> antennaConnected only; see utils/liftIndicators.js
 *       for the colour rules. Deliberately not age-aware — the antenna state
 *       is reported as at the last heartbeat however old that is, which is
 *       why the copy points readers at Heartbeat Tracking.
 *   Heartbeat Tracking  -> validLocationAgeHours, derived from
 *       DUO_METRIC_REPORTING.LAST_VALID_LOCATION_DATE; see
 *       utils/liftIndicators.js. The 7-day threshold is STALE_DAYS there and
 *       PBC expect to revisit it, so this copy and that constant have to move
 *       together. The same rule drives the Lift Map pin colours, so changes
 *       here are visible on both surfaces.
 *
 * TE-2066: the "Last Valid Location" column and its definition were removed —
 * the colour dots made it redundant. lastValidLocationDate is still the source
 * for Heartbeat Tracking and is surfaced in that indicator's hover detail, and
 * the Lift Map InfoWindow still labels it explicitly.
 *   Location            -> inDcGeofence (haversine distance vs the DC's
 *       FENCE_RADIUS_METERS, computed in getLiftsDetailList) then routeName.
 *   Firmware Version    -> firmwareVersion as reported by the tracker.
 *
 * Shape is {name, desc} to match the existing Route Status definitions array
 * and the shared AdditionalInfoDialog renderer.
 */
export const LIFT_FIELD_DEFINITIONS = [
  // TE-2066: the "Last Valid Location" entry is removed alongside its column.
  // The timestamp it described is still shown, in the Heartbeat Tracking hover
  // detail, and that indicator's own entry below explains it.
  {
    name: "Last Updated",
    desc:
      "The timestamp of the most recent communication received from the lift, " +
      "whether or not it included a GPS location. This indicates when the " +
      "lift last communicated.",
  },
  {
    name: "Asset Tracking",
    desc:
      "The status of the lift's RFID antenna at its last communication " +
      "received from the lift. Green = connected, red = disconnected, " +
      "grey = the lift is Spare and not in service, and n/a = no status " +
      "reported. A red indicator may mean pallet RFID scan events are missing " +
      "even though other lift telemetry is still being received. This shows " +
      "the antenna as at the lift's last heartbeat, however long ago that " +
      "was, so check Heartbeat Tracking to see whether the lift is still " +
      "reporting. Hover the indicator for that timestamp.",
  },
  {
    name: "Heartbeat Tracking",
    desc:
      "How recently the lift reported its location. Green = a valid GPS " +
      "location was reported within the last 7 days. Red = more than 7 days " +
      "since the last valid location. Grey = the lift is Spare and not in " +
      "service. n/a = the lift has yet to report a location. These are the " +
      "same colours used for the pins on the Lift Map. Hover the indicator " +
      "for both the last GPS fix and the last communication.",
  },
  {
    name: "Location",
    desc:
      "The lift's location at its last communication received from the lift. " +
      "Inside a DC geofence, the DC name is shown. Outside a DC geofence, the " +
      "route number is shown when available; otherwise, \"On Route\" is displayed.",
  },
  {
    name: "Firmware Version",
    desc:
      "The firmware version currently reported by the lift's tracker. To " +
      "request an upgrade, ensure the lift is powered on and the tracker is " +
      "connected to the cellular network, then contact support@rehrig.com",
  },
];

export default LIFT_FIELD_DEFINITIONS;
